import type { UTCDate } from '@date-fns/utc';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useMemo } from 'react';

import {
  COMPOSITION_GET_DATA_V3,
  COMPOSITION_HEADCOUNT_ONLY_V3,
  type CompositionHeadcountOnlyV3Query,
  type CompositionV3BreakdownOptions,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  type CompositionV3Ent,
  type CompositionV3Filters,
  type CompositionV3Req,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useUserPreferences } from '../../../../../shared/user-preferences/hooks/useUserPreferences';
import { getPreferenceScope } from '../../../../../shared/user-preferences/utils/preferences';
import { getDateRangeFromPreset } from '../../../filters/constants/datePresets';
import { CORRECTION_PREFERENCE_FILTER_IDS } from '../../../filters/constants/filter-constants';
import {
  useFilterContext,
  useIsTaxonomyResolved,
} from '../../../filters/contexts/FilterProvider';
import { useAvailableDateRangeWithTrialLag } from '../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import {
  type Filter,
  type FilterId,
  type FilterSelectionsMap,
  FilterType,
  type PrimaryFilterId,
  type StaticListFilter,
} from '../../../filters/types/filters';
import {
  activityBreakdownLevelFromFilters,
  getBaseCompositionV3BreakdownLevels,
  getCompositionV3BreakdownOptions,
} from '../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import { getCompositionV3EntityNamesByKey } from '../../../filters/utils/composition-v3/getCompositionV3Entities';
import {
  getCompositionV3Variables,
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { isCompositionV3CustomSkillTaxonomyReady } from '../../../filters/utils/composition-v3/is-composition-v3-custom-skill-taxonomy-ready';
import { plotBreakdownLevelsToOverrides } from '../../../filters/utils/composition-v3/plotBreakdownLevelsToOverrides';
import { getFormattedDateString } from '../../../filters/utils/dateUtils';
import {
  getMonthRefsFromV3Filters,
  getV3MonthRefs,
} from '../../../filters/utils/get-v3-month-refs';
import {
  effectiveFiltersWithSinglePrimaryEntity,
  useChartPrimaryFilter,
} from '../../../hooks/useChartPrimaryFilter';
import { ChartLoader } from '../../Chart/ChartLoader';
import { useChart } from '../../Chart/ChartProvider';
import { useCompositionV3QueryWithStatus } from '../../DataResolver/use-composition-v3-query';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { SecondaryFilterConfig } from '../../PageConfigProvider/page-reducer';
import { RevelioPageId } from '../../PageConfigProvider/types';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import {
  type PositionedItem,
  plotUsesShareOfSegmentPageSelections,
} from '../../PageConfigProvider/types/item';
import { usePageState } from '../../PageState/PageStateProvider';
import { haveSelectionsBeenModified } from '../../PageState/types';
import type { DataProviderChildren, DataProviderResult } from '../types';
import {
  areStringArraysEqual,
  useStableReference,
} from '../utils/use-stable-reference';
import { amendCompositionV3WithMissingEntities } from './composition-v3/amend-composition-v3-missing-entities';
import { compositionV3DataQueryFor } from './composition-v3/composition-v3-graphql-response';
import {
  areEntityNameMapsEqual,
  areRequestedEntitiesEqual,
} from './composition-v3/composition-v3-request-identity';
import {
  buildExhaustiveBreakdownDimensionsKey,
  useExhaustiveBreakdownIdsV3,
} from './composition-v3/useExhaustiveBreakdownIdsV3';
import {
  useOverviewSharedRequest,
  withOverviewSharedBreakdowns,
} from './overview-shared-request';

export function resolveCompositionV3RequestFilters({
  filters,
  isTimeseries,
  revelioPageId,
  dateRangeDefault,
  availableDateRange,
}: {
  filters: CompositionV3Filters;
  isTimeseries: boolean;
  revelioPageId?: RevelioPageId;
  dateRangeDefault?: string;
  availableDateRange?: {
    month?: { min?: UTCDate | null; max?: UTCDate | null };
  } | null;
}): CompositionV3Filters {
  const { start_date, end_date } = filters;
  const hasMultiMonthRange =
    Boolean(start_date) && Boolean(end_date) && start_date !== end_date;

  if (hasMultiMonthRange) {
    return filters;
  }

  if (
    revelioPageId === RevelioPageId.COMPANY_OVERVIEW &&
    isTimeseries &&
    dateRangeDefault &&
    availableDateRange?.month?.min &&
    availableDateRange?.month?.max
  ) {
    const { start, end } = getDateRangeFromPreset(
      dateRangeDefault,
      availableDateRange.month.max,
      availableDateRange.month.min
    );
    return {
      ...filters,
      start_date: getFormattedDateString(start, 'month'),
      end_date: getFormattedDateString(end, 'month'),
    };
  }

  const systemMax = availableDateRange?.month?.max;
  const systemMin = availableDateRange?.month?.min;
  const resolvedEnd =
    end_date ?? (systemMax ? getFormattedDateString(systemMax, 'month') : '');
  const resolvedStart =
    start_date ?? (systemMin ? getFormattedDateString(systemMin, 'month') : '');

  if (resolvedStart === '' || resolvedEnd === '') {
    return filters;
  }

  return {
    ...filters,
    start_date: resolvedStart,
    end_date: resolvedEnd,
  };
}

export function useCompositionV3Setup() {
  const {
    filterState,
    activeFilterMap,
    geographySelectionListIds,
    geographyTaxonomyId,
    skillTaxonomyId,
    skillTaxonomyVersion,
  } = useFilterContext();
  const { config, items: tabItems, revelioPageId } = useTabContentConfig();
  const preferenceScope = getPreferenceScope({
    pageId: revelioPageId,
    primaryFilterId: filterState.activeKey ?? 'NO_PRIMARY_FILTER',
  });
  const { isInitialLoading: isPreferencesInitialLoading } =
    useUserPreferences(preferenceScope);
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const {
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  } = useCustomSkillTaxonomyTree(skillTaxonomyId, skillTaxonomyVersion);

  return {
    filterState,
    activeFilterMap,
    config,
    tabItems,
    revelioPageId,
    preferenceScope,
    isPreferencesInitialLoading,
    effectiveFilters,
    geographySelectionListIds,
    geographyTaxonomyId,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  };
}

type CompositionV3QueryResult =
  | CompositionV3DataQuery
  | CompositionHeadcountOnlyV3Query;

type CompositionV3DataProviderProps = {
  primaryDimension: PrimaryFilterId;
  isTimeseries?: boolean;
  entitiesOverride?: CompositionV3Ent[];
  getSelectionBreakdowns?: boolean;
  requireFullMetrics?: boolean;
  additionalEffectiveFilters?: Filter[];
  breakdownOptionsPatch?: Partial<CompositionV3BreakdownOptions>;
  secondaryDimension?: SecondaryDimensionValues;
  children: DataProviderChildren<CompositionV3QueryResult | undefined>;
};

type UseCompositionRawDataParams = Omit<
  CompositionV3DataProviderProps,
  'children' | 'getSelectionBreakdowns'
> & {
  pause?: boolean;
};

type CompositionRawDataResult = DataProviderResult<
  CompositionV3QueryResult | undefined
> & {
  isReady: boolean;
};

export const CompositionRawDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  getSelectionBreakdowns,
  requireFullMetrics = false,
  additionalEffectiveFilters,
  breakdownOptionsPatch,
  secondaryDimension,
  children,
}: CompositionV3DataProviderProps) => {
  if (getSelectionBreakdowns) {
    return (
      <CompositionV3BreakdownDataProvider
        primaryDimension={primaryDimension}
        isTimeseries={isTimeseries}
        entitiesOverride={entitiesOverride}
        additionalEffectiveFilters={additionalEffectiveFilters}
      >
        {children}
      </CompositionV3BreakdownDataProvider>
    );
  }

  return (
    <CompositionV3NonBreakdownDataProvider
      primaryDimension={primaryDimension}
      isTimeseries={isTimeseries}
      entitiesOverride={entitiesOverride}
      requireFullMetrics={requireFullMetrics}
      additionalEffectiveFilters={additionalEffectiveFilters}
      breakdownOptionsPatch={breakdownOptionsPatch}
      secondaryDimension={secondaryDimension}
    >
      {children}
    </CompositionV3NonBreakdownDataProvider>
  );
};

const CompositionV3NonBreakdownDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  requireFullMetrics = false,
  additionalEffectiveFilters,
  breakdownOptionsPatch,
  secondaryDimension,
  children,
}: CompositionV3DataProviderProps) => {
  const { isReady, ...result } = useCompositionRawData({
    primaryDimension,
    isTimeseries,
    entitiesOverride,
    requireFullMetrics,
    additionalEffectiveFilters,
    breakdownOptionsPatch,
    secondaryDimension,
  });

  if (!isReady) {
    return <ChartLoader />;
  }

  return children(result);
};

export const useCompositionRawData = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  requireFullMetrics = false,
  additionalEffectiveFilters,
  breakdownOptionsPatch,
  secondaryDimension,
  pause = false,
}: UseCompositionRawDataParams): CompositionRawDataResult => {
  const setupContext = useCompositionV3Setup();
  const {
    revelioPageId,
    config,
    activeFilterMap,
    isPreferencesInitialLoading,
    effectiveFilters,
    geographySelectionListIds,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  } = setupContext;
  const effectiveFiltersWithAdditional = additionalEffectiveFilters?.length
    ? [...effectiveFilters, ...additionalEffectiveFilters]
    : effectiveFilters;
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();

  const customSkillTaxonomyReady = isCompositionV3CustomSkillTaxonomyReady({
    effectiveFilters: effectiveFiltersWithAdditional,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  });

  const useHeadcountOnly =
    !requireFullMetrics && revelioPageId === RevelioPageId.COMPANY_OVERVIEW;

  const sharesPageRequest = useOverviewSharedRequest();
  const sharedRequest = useHeadcountOnly ? null : sharesPageRequest;
  const breaksDownActivities = secondaryDimension === 'activities';
  const pageQuery = sharedRequest
    ? sharedRequest.query
    : compositionV3DataQueryFor(secondaryDimension);
  const query = useHeadcountOnly ? COMPOSITION_HEADCOUNT_ONLY_V3 : pageQuery;

  const breakdownOptionsOverride = useHeadcountOnly
    ? getBaseCompositionV3BreakdownLevels()
    : undefined;

  const baseVariables = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters: effectiveFiltersWithAdditional,
    isSnapshot: !isTimeseries,
    geographySelectionListIds,
    getSelectionBreakdowns: false,
    customSkillTree,
    compositionEntityScopes: entitiesOverride,
    breakdownOptionsOverride,
    activeFilterMap,
  });

  const breakdownOptions = {
    ...baseVariables.breakdown_options,
    ...(breaksDownActivities && {
      activity_breakdown_level: activityBreakdownLevelFromFilters(
        baseVariables.filters
      ),
    }),
    ...breakdownOptionsPatch,
  };

  const requestVariables: CompositionV3Req = {
    ...baseVariables,
    breakdown_options: breakdownOptions,
    filters: resolveCompositionV3RequestFilters({
      filters: baseVariables.filters,
      isTimeseries,
      revelioPageId,
      dateRangeDefault: config.dateRangeDefault,
      availableDateRange,
    }),
  };
  const variables = sharedRequest
    ? withOverviewSharedBreakdowns(requestVariables, sharedRequest)
    : requestVariables;

  const monthRefs = getV3MonthRefs({
    availableDateRange,
    filters: variables.filters,
  });

  const entityNamesByKey = entitiesOverride
    ? undefined
    : getCompositionV3EntityNamesByKey({
        primaryDimension,
        effectiveFilters,
      });

  const correctionPreferencesReady = areCorrectionPreferencesReady(
    config.secondaryFilters,
    activeFilterMap
  );

  const isReady =
    customSkillTaxonomyReady &&
    isCompositionV3QueryVariablesReady(variables) &&
    !isPreferencesInitialLoading &&
    correctionPreferencesReady;

  const result = useCompositionV3Query({
    query,
    variables,
    monthRefs,
    entityNamesByKey,
    pause: pause || !isReady,
  });

  return { ...result, isReady };
};

const CompositionV3BreakdownDataProvider = ({
  primaryDimension,
  isTimeseries = false,
  entitiesOverride,
  additionalEffectiveFilters,
  children,
}: CompositionV3DataProviderProps) => {
  const setupContext = useCompositionV3Setup();
  const {
    config,
    tabItems,
    activeFilterMap,
    revelioPageId: pageId,
    isPreferencesInitialLoading,
    effectiveFilters,
    geographySelectionListIds,
    geographyTaxonomyId,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  } = setupContext;
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const chartPrimaryFilter = useChartPrimaryFilter({ primaryDimension });
  const { item } = useChart();
  const effectiveFiltersWithSingleEntity = (() => {
    const base = effectiveFiltersWithSinglePrimaryEntity({
      item,
      effectiveFilters,
      chartPrimaryFilter,
    });
    return additionalEffectiveFilters?.length
      ? [...base, ...additionalEffectiveFilters]
      : base;
  })();

  const entityNamesByKey = entitiesOverride
    ? undefined
    : getCompositionV3EntityNamesByKey({
        primaryDimension,
        effectiveFilters: effectiveFiltersWithSingleEntity,
      });
  const { pageState } = usePageState();
  const isModified = item?.id
    ? haveSelectionsBeenModified(pageState, item.id)
    : false;

  const shareOfSegmentPlotsNeedingSubfilters = tabItems.filter((chartItem) => {
    if (!plotUsesShareOfSegmentPageSelections(chartItem)) return false;
    const key = canonicalizeBreakdownKey(chartItem.plot?.secondaryDimension);
    return (
      key !== null &&
      (SUPPORTED_BREAKDOWN_KEYS.has(key) || key === STATE_OVER_MSA_KEY)
    );
  });

  const subFilterSelectionsByPlotId = (() => {
    const base: Record<string, SelectionListEntry[] | undefined> = {
      ...(isModified
        ? {
            ...pageState.initialSelections,
            [item.id]: pageState.selections?.[item?.id],
          }
        : pageState.initialSelections),
    };
    shareOfSegmentPlotsNeedingSubfilters.forEach((p) => {
      if (base[p.id] === undefined) {
        base[p.id] = [];
      }
    });
    return base;
  })();

  const allBreakdownSelectionsReady = areAllBreakdownSelectionsReadyV3(
    tabItems,
    subFilterSelectionsByPlotId
  );

  const baseVariables = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters: effectiveFiltersWithSingleEntity,
    getSelectionBreakdowns: false,
    isSnapshot: !isTimeseries,
    customSkillTree,
    geographySelectionListIds,
    compositionEntityScopes: entitiesOverride,
    activeFilterMap,
  });

  const normalizedEffectiveForBreakdown = normalizeCompositionEffectiveFilters(
    effectiveFiltersWithSingleEntity
  );

  const customSkillDeepestLevel = getCustomSkillDeepestLevelFromFilters(
    normalizedEffectiveForBreakdown
  );

  const plotFilters = item?.plot?.plotFilters;
  const secondaryDimension = item?.plot?.secondaryDimension;
  const breakdownLevels = item?.plot?.breakdownLevels;

  const breakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: baseVariables.filters,
    customSkillTree,
    customSkillDeepestLevel,
    geographySelectionListIds,
    // Per-chart granularity override (the AI share pipeline sets it, so "share
    // in the US" charts countries and "the US workforce by state" charts US
    // places rather than the region default). `item` is THIS chart's own
    // config (useChart), so an override cannot leak to sibling charts on the
    // same page; it is undefined for every chart that doesn't carry the
    // field, preserving the drill-down default.
    breakdownLevelOverrides: plotBreakdownLevelsToOverrides(
      breakdownLevels,
      geographySelectionListIds
    ),
    plotFilters,
    secondaryDimension,
  });

  const exhaustiveBreakdownDimensionsKey = useMemo(
    () =>
      buildExhaustiveBreakdownDimensionsKey(
        tabItems
          .filter(plotUsesShareOfSegmentPageSelections)
          .map((chartItem) => chartItem.plot?.secondaryDimension)
      ),
    [tabItems]
  );

  const { exhaustiveBreakdownIds, isExhaustiveBreakdownIdsReady } =
    useExhaustiveBreakdownIdsV3({
      geographyTaxonomyId,
      secondaryDimensionsKey: exhaustiveBreakdownDimensionsKey,
      compositionFilters: baseVariables.filters,
      geographySelectionListIds,
      customSkillTree,
      customSkillDeepestLevel,
      isCustomSkillTaxonomyPending,
    });

  const requestVariables: CompositionV3Req = {
    ...baseVariables,
    filters: resolveCompositionV3RequestFilters({
      filters: baseVariables.filters,
      isTimeseries,
      revelioPageId: pageId,
      dateRangeDefault: config.dateRangeDefault,
      availableDateRange,
    }),
    breakdown_options: {
      ...breakdownOptions,
      ...(exhaustiveBreakdownIds ?? {}),
    },
  };
  const sharedRequest = useOverviewSharedRequest();
  const variables = sharedRequest
    ? withOverviewSharedBreakdowns(requestVariables, sharedRequest)
    : requestVariables;

  const customSkillTaxonomyReady = isCompositionV3CustomSkillTaxonomyReady({
    effectiveFilters: effectiveFiltersWithSingleEntity,
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  });

  const entities = variables.entities;
  const entitiesEmpty = Array.isArray(entities) && entities.length === 0;

  const monthRefs = getV3MonthRefs({
    availableDateRange,
    filters: variables.filters,
  });

  const correctionPreferencesReady = areCorrectionPreferencesReady(
    config.secondaryFilters,
    activeFilterMap
  );

  if (
    !customSkillTaxonomyReady ||
    !isExhaustiveBreakdownIdsReady ||
    !allBreakdownSelectionsReady ||
    !isCompositionV3QueryVariablesReady(variables) ||
    isPreferencesInitialLoading ||
    !correctionPreferencesReady ||
    entitiesEmpty
  ) {
    return <ChartLoader />;
  }

  return (
    <CompositionV3Query
      query={
        sharedRequest
          ? sharedRequest.query
          : compositionV3DataQueryFor(secondaryDimension)
      }
      variables={variables}
      monthRefs={monthRefs}
      haveSelectionsBeenModified={isModified}
      entityNamesByKey={entityNamesByKey}
    >
      {children}
    </CompositionV3Query>
  );
};

function areCompositionV3FilterDatesReady(
  filters: CompositionV3Req['filters']
): boolean {
  const { start_date, end_date } = filters;
  if (
    start_date === null ||
    start_date === undefined ||
    end_date === null ||
    end_date === undefined
  ) {
    return false;
  }
  if (start_date === '' || end_date === '') {
    return false;
  }
  return true;
}

function areCompositionV3CorrectionsReady(
  corrections: CompositionV3Req['corrections']
): boolean {
  const { with_contingent_workers, with_weight, with_sample_weight } =
    corrections;
  const hasWorkforceChoice =
    typeof with_contingent_workers === 'boolean' ||
    typeof corrections.workforce_type === 'string';
  return (
    hasWorkforceChoice &&
    typeof with_weight === 'boolean' &&
    typeof with_sample_weight === 'boolean'
  );
}

export function isCompositionV3QueryVariablesReady(
  variables: CompositionV3Req
): boolean {
  return (
    areCompositionV3FilterDatesReady(variables.filters) &&
    areCompositionV3CorrectionsReady(variables.corrections)
  );
}

/**
 * Correction-preference filters (Data Adjustments / Contingent Workers) are
 * hydrated asynchronously from the user's saved preferences. We only need to
 * wait for that sync when the page actually declares those filters: pages that
 * omit them (e.g. AI-generated layouts) never receive selections, so they
 * should render immediately with the default corrections rather than waiting
 * forever.
 */
export function areCorrectionPreferencesReady(
  secondaryFilters: SecondaryFilterConfig[],
  activeFilterMap: FilterSelectionsMap
): boolean {
  const hasSelections = (id: FilterId): boolean => {
    const filter = activeFilterMap.get(id) as StaticListFilter | undefined;
    return (
      filter?.type === FilterType.STATIC_LIST && filter.selections.length > 0
    );
  };
  return CORRECTION_PREFERENCE_FILTER_IDS.every((id) => {
    const isDeclaredOnPage = secondaryFilters.some(
      (secondaryFilter) => secondaryFilter.id === id
    );
    return !isDeclaredOnPage || hasSelections(id);
  });
}

const CompositionV3Query = ({
  query = COMPOSITION_GET_DATA_V3,
  variables,
  monthRefs: monthRefsProp,
  children,
  haveSelectionsBeenModified,
  entityNamesByKey,
}: {
  query?: TypedDocumentNode<
    CompositionV3QueryResult,
    CompositionV3DataQueryVariables
  >;
  variables: CompositionV3Req;
  monthRefs?: string[];
  children: DataProviderChildren<CompositionV3QueryResult | undefined>;
  haveSelectionsBeenModified?: boolean;
  entityNamesByKey?: ReadonlyMap<string, string>;
}) => {
  const result = useCompositionV3Query({
    query,
    variables,
    monthRefs: monthRefsProp,
    haveSelectionsBeenModified,
    entityNamesByKey,
  });

  return children(result);
};

const useCompositionV3Query = ({
  query = COMPOSITION_GET_DATA_V3,
  variables,
  monthRefs: monthRefsProp,
  haveSelectionsBeenModified,
  entityNamesByKey,
  pause = false,
}: {
  query?: TypedDocumentNode<
    CompositionV3QueryResult,
    CompositionV3DataQueryVariables
  >;
  variables: CompositionV3Req;
  monthRefs?: string[];
  haveSelectionsBeenModified?: boolean;
  entityNamesByKey?: ReadonlyMap<string, string>;
  pause?: boolean;
}): DataProviderResult<CompositionV3QueryResult | undefined> => {
  const isTaxonomyResolved = useIsTaxonomyResolved();
  const [urqlState] = useCompositionV3QueryWithStatus({
    query,
    variables: { req: variables },
    pause:
      pause ||
      !isTaxonomyResolved ||
      !isCompositionV3QueryVariablesReady(variables),
  });
  const { data, status, error } = urqlState;
  const monthRefs = useStableReference(
    monthRefsProp ?? getMonthRefsFromV3Filters(variables.filters),
    areStringArraysEqual
  );

  const requestedEntities = useStableReference(
    Array.isArray(variables.entities)
      ? variables.entities
      : [variables.entities],
    areRequestedEntitiesEqual
  );
  const stableEntityNamesByKey = useStableReference(
    entityNamesByKey,
    areEntityNameMapsEqual
  );
  const amendedData = useMemo(
    () =>
      amendCompositionV3WithMissingEntities({
        data,
        requestedEntities,
        entityNamesByKey: stableEntityNamesByKey,
      }),
    [data, requestedEntities, stableEntityNamesByKey]
  );

  return {
    data: amendedData,
    status,
    error: error ?? undefined,
    haveSelectionsBeenModified,
    monthRefs,
  };
};

type SelectionListEntry = { id: string };

type SubFilterSelectionsByPlotId =
  | Record<string, SelectionListEntry[] | undefined>
  | undefined;

const SUPPORTED_BREAKDOWN_KEYS = new Set([
  'Geography',
  'Rics',
  'Role',
  'Skill',
]);
const SKILL_KEY = 'Skill';
const STATE_OVER_MSA_KEY = 'StateOverMsa';

const areAllBreakdownSelectionsReadyV3 = (
  configItems: PositionedItem[],
  subFilterSelections: SubFilterSelectionsByPlotId
): boolean => {
  const plotsNeedingSelections = configItems.filter((item) => {
    if (!plotUsesShareOfSegmentPageSelections(item)) return false;
    const key = canonicalizeBreakdownKey(item.plot?.secondaryDimension);
    return (
      key !== null &&
      (SUPPORTED_BREAKDOWN_KEYS.has(key) || key === STATE_OVER_MSA_KEY)
    );
  });

  if (plotsNeedingSelections.length === 0) return true;

  return plotsNeedingSelections.every(
    (item) => subFilterSelections?.[item.id] !== undefined
  );
};

const canonicalizeBreakdownKey = (key?: string): string | null => {
  if (!key) {
    return null;
  }

  const normalizedKey = key.toLowerCase();
  if (normalizedKey === 'skills' || normalizedKey === 'skill') {
    return SKILL_KEY;
  }

  return key.charAt(0).toUpperCase() + key.slice(1);
};
